import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  FALLBACK_LEGAL_LINKS,
  legalDocumentPath,
} from '@/constants/legalDocuments';
import { legalService } from '@/services/legalService';

export const LEGAL_DOCUMENTS_QUERY_KEY = ['legal-documents'] as const;

/**
 * The legal documents the admin has made live. This list is the only source of
 * which documents exist, so every legal surface renders from it — publishing,
 * renaming or hiding one reaches visitors on their next page load, with no deploy.
 */
export function useLegalDocuments() {
  return useQuery({
    queryKey: LEGAL_DOCUMENTS_QUERY_KEY,
    queryFn: () => legalService.getDocuments(),
    staleTime: 30 * 60 * 1000,
  });
}

/**
 * The footer's legal links: whatever the admin has published and made live — no fixed list, so
 * a document added or hidden there appears or disappears without a deploy.
 *
 * Empty means the documents are not live yet (or the request failed), and a footer with no
 * Terms and no Privacy Policy is not a state we may ship — so it falls back to the urls that
 * shipped before the admin panel owned this.
 */
export function useLegalLinks() {
  const { data: legalDocuments } = useLegalDocuments();
  return useMemo(() => {
    const published = (legalDocuments ?? []).map(document => ({
      labelKey: document.key,
      label: document.title,
      href: legalDocumentPath(document.key),
    }));
    return published.length > 0 ? published : FALLBACK_LEGAL_LINKS;
  }, [legalDocuments]);
}
